import React from "react";

import {
  Button,
  Col,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row,
} from "reactstrap";
import useDemandsList from "../../../hooks/useDemandsList";
import useEmployeesList from "../../../hooks/useEmployeesList";
import useTeamsList from "../../../hooks/useTeamsList";
import useTerritorializationsList from "../../../hooks/useTerritorializationsList";
import useTypeLayersList from "../../../hooks/useTypeLayersList";
import useTrapTypesList from "../../../hooks/useTrapTypesList";
import { getTodayEpidemiologicalWeek } from "../../../services/utils/todayEpidemiologicalWeek";
import { DateFilter } from "../../../components/Filters/DateFilter";
import YearsFilter from "../../../components/Filters/YearsFilter";
import { EpidemiologicWeekFilter } from "../../../components/Filters/EpidemiologicWeekFilter";
import TrapTypesFilter from "../../../components/Filters/TrapTypesFilter";
import DemandsFilter from "../../../components/Filters/DemandsFilter";
import UsersFilter from "../../../components/Filters/UsersFilter";
import TeamsFilter from "../../../components/Filters/TeamsFilter";
import TypeLayersFilter from "../../../components/Filters/TypeLayersFilter";
import TerritorializationsFilter from "../../../components/Filters/TerritorializationsFilter";
import { MonthWeekFilter } from "../../../components/Filters/MonthWeekFilter";
import { toast } from "react-toastify";
import {
  fetchTrapTypesData,
  getTerritorializationsIdsToSendOnRequest,
  getUsersIdsToSendOnRequest,
  monthsOptionsList,
} from "../../../services/utils/globalFunctions";
import getWeeks from "../../../services/utils/epidemiologicalWeek";
import { ExchangeIcon } from "../../../components/Icons/ExchangeIcon";
import defaultStyles from "./ProductivityPage.module.css";
import { getUserData } from "../../../services/utils/auth";
import moment from "moment";
import {
  fetchProductivityByAreas,
  fetchTotalProductivity,
} from "../../../services/api/Trap";
import { useProductivity } from "./Hooks/useProductivity";
import useLoading from "./Hooks/useLoading";
import useAppliedFilters from "./Hooks/useAppliedFilters";
import {
  fetchConsolidatedVisits,
  fetchVisitsByStatus,
} from "../../../services/api/Visits";
import { fetchGoalsByFilters } from "../../../services/api/Goals";
import LoadingSpin from "react-loading-spin";

const useProductivityPageHeader = () => {
  const { demandsList } = useDemandsList();
  const { employeesList } = useEmployeesList();
  const { teamsList } = useTeamsList();
  const { territorializationsList } = useTerritorializationsList();
  const { trapTypesList } = useTrapTypesList();
  const { typeLayersList } = useTypeLayersList();

  return {
    demandsList,
    employeesList,
    teamsList,
    territorializationsList,
    trapTypesList,
    typeLayersList,
  };
};

export const ProductivityPageHeader = () => {
  const {
    demandsList,
    employeesList,
    teamsList,
    territorializationsList,
    trapTypesList,
    typeLayersList,
  } = useProductivityPageHeader();

  return (
    <section className={defaultStyles.headerContainer}>
      <ProductivityPageFilters
        demands={demandsList}
        employees={employeesList}
        teams={teamsList}
        territorializations={territorializationsList}
        trapTypes={trapTypesList}
        typeLayers={typeLayersList}
      />
      <DownloadInformations />
    </section>
  );
};

const DownloadInformations = () => {
  return (
    <>
      <Button
        disabled
        id="downloadPopover"
        className={defaultStyles.downloadButton}
      >
        <i>
          <img
            src={require("../../../assets/img/icons/interface/download.png")}
            style={{ width: "16px", height: "16px" }}
            alt="icone"
          />
        </i>{" "}
        Baixar dados brutos
      </Button>
    </>
  );
};

const useProductivityPageFilters = () => {
  const [filters, setFilters] = React.useState(
    PRODUCTIVITY_PAGE_DEFAULT_FILTERS
  );
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const { setProductivityData } = useProductivity();
  const { isInformationsLoading, setIsInformationsLoading } = useLoading();
  const { setAppliedFilters } = useAppliedFilters();
  const { setTrapTypesList, trapTypesList } = useTrapTypesList();

  React.useEffect(() => {
    fetchAllComponentsData();
  }, []);

  const togglePopover = () => setIsPopoverOpen(!isPopoverOpen);

  const fetchAllComponentsData = async () => {
    const beginDate = moment(filters.beginDate);
    const endDate = moment(filters.endDate);

    let trapTypes = [];

    if (trapTypesList.length === 0) {
      const trapTypesResponse = await fetchTrapTypesData(setTrapTypesList);

      trapTypes = trapTypesResponse;
    }

    if (beginDate.isAfter(endDate)) {
      toast.error("A data inicial deve ser menor ou igual que a data final");
      return;
    }

    if (
      endDate.diff(beginDate, "days") > 31 &&
      getUserData("organizationId") === "559a16ab-5da8-41ba-96b8-6e9f55feddd8"
    ) {
      toast.error("O intervalo de datas deve ser menor ou igual a 31 dias");
      return;
    }

    setAppliedFilters(filters);
    setIsInformationsLoading(true);

    try {
      const [
        totalProductivity,
        visitsStatus,
        consolidatedVisits,
        goals,
        productivityByAreas,
      ] = await Promise.all([
        fetchTotalProductivityData(),
        fetchVisitsStatusData(),
        fetchConsolidatedVisitsData(),
        fetchGoalsData(),
        fetchProductivityByAreasData(trapTypes),
      ]);

      setProductivityData({
        fieldProductivity: totalProductivity.fieldProductivity,
        typingProductivity: totalProductivity.typingProductivity,
        visitsStatus: visitsStatus.visits,
        consolidatedVisits: consolidatedVisits.consolidatedVisits,
        goals: goals,
        productivityByAreas,
      });
    } catch (error) {
      toast.error("Ocorreu um erro ao buscar os dados");
      console.error(error);
    } finally {
      setIsInformationsLoading(false);
    }
  };

  const fetchProductivityByAreasData = async (trapTypes) => {
    if (filters.territorializations.length === 0) return [];

    const requestFilters = {
      demandsIds: [],
      organizationId: getUserData("organizationId"),
      periods: [
        {
          beginDate: moment
            .utc(filters.beginDate)
            .set({ hour: 0, minute: 0, second: 0 })
            .toISOString(),
          endDate: moment
            .utc(filters.finalDate)
            .set({ hour: 23, minute: 59, second: 59 })
            .toISOString(),
          name: `${moment(filters.beginDate).format("DD/MM/YYYY")} à ${moment(
            filters.finalDate
          ).format("DD/MM/YYYY")}`,
        },
      ],
      projectsIds: [],
      propertiesTypes: [],
      territorializationsIds: filters.territorializations.map(
        ({ value }) => value
      ),
      trapTypesIds: trapTypes.map(({ value }) => value),
      usersIds: [],
    };

    try {
      const { data, status } = await fetchProductivityByAreas(requestFilters);
      if (status !== 200)
        throw new Error(
          "Ocorreu um erro ao buscar pela produtividade das digitações. Verifique sua conexão com a internet e caso persista entre em contato consco."
        );

      return data;
    } catch (error) {
      console.error(error.message);
      toast.error(
        "Ocorreu um erro ao buscar pela produtividade das digitações. Verifique sua conexão com a internet e caso persista entre em contato consco."
      );
    }
  };

  const fetchTotalProductivityData = async () => {
    const requestFilters = {
      periods: {
        beginDate: moment
          .utc(filters.beginDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString(),
        finalDate: moment
          .utc(filters.finalDate)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString(),
      },
      organizationId: getUserData("organizationId"),
      usersIds: getUsersIdsToSendOnRequest(filters),
      territorializationsIds: getTerritorializationsIdsToSendOnRequest(filters),
    };

    try {
      const { data, status } = await fetchTotalProductivity(requestFilters);
      if (status !== 200)
        throw new Error(
          "Ocorreu um erro ao buscar pela produtividade das digitações. Verifique sua conexão com a internet e caso persista entre em contato consco."
        );

      return data;
    } catch (error) {
      console.error(error.message);
      toast.error(
        "Ocorreu um erro ao buscar pela produtividade das digitações. Verifique sua conexão com a internet e caso persista entre em contato consco."
      );
    }
  };

  const fetchVisitsStatusData = async () => {
    const requestFilters = {
      periods: {
        beginDate: moment
          .utc(filters.beginDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString(),
        finalDate: moment
          .utc(filters.finalDate)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString(),
      },
      organizationId: getUserData("organizationId"),
      usersIds: getUsersIdsToSendOnRequest(filters),
      territorializationsIds: getTerritorializationsIdsToSendOnRequest(filters),
    };

    try {
      const { data, status } = await fetchVisitsByStatus(requestFilters);
      if (status !== 200)
        throw new Error(
          "Ocorreu um erro ao buscar pelos dados das visitas. Verifique sua conexão com a internet e caso persista entre em contato consco."
        );

      return data;
    } catch (error) {
      console.error(error.message);
      toast.error(
        "Ocorreu um erro ao buscar pelos dados das visitas. Verifique sua conexão com a internet e caso persista entre em contato consco."
      );
    }
  };

  const fetchConsolidatedVisitsData = async () => {
    const requestFilters = {
      periods: {
        beginDate: moment
          .utc(filters.beginDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString(),
        finalDate: moment
          .utc(filters.finalDate)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString(),
      },
      organizationId: getUserData("organizationId"),
      usersIds: getUsersIdsToSendOnRequest(filters),
      territorializationsIds: getTerritorializationsIdsToSendOnRequest(filters),
    };

    try {
      const { data, status } = await fetchConsolidatedVisits(requestFilters);
      if (status !== 200)
        throw new Error(
          "Ocorreu um erro ao buscar pelos dados das visitas consolidadas. Verifique sua conexão com a internet e caso persista entre em contato consco."
        );

      return data;
    } catch (error) {
      console.error(error.message);
      toast.error(
        "Ocorreu um erro ao buscar pelos dados das visitas consolidadas. Verifique sua conexão com a internet e caso persista entre em contato consco."
      );
    }
  };

  const fetchGoalsData = async () => {
    const requestFilters = {
      periods: {
        beginDate: moment
          .utc(filters.beginDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString(),
        finalDate: moment
          .utc(filters.finalDate)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString(),
      },
      organizationId: getUserData("organizationId"),
      usersIds: getUsersIdsToSendOnRequest(filters),
      territorializationsIds: getTerritorializationsIdsToSendOnRequest(filters),
    };

    try {
      const { data, status } = await fetchGoalsByFilters(requestFilters);

      if (status !== 200)
        throw new Error(
          "Ocorreu um erro ao buscar pelos dados de metas. Verifique sua conexão com a internet e tente novamente. Caso o problema persista, entre em contato com nossa equipe."
        );

      return data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
        toast.error(error.message);
      }
    }
  };

  const handleEpidemiologicWeeksFilterChange = (filterName, newValue) => {
    setFilters((previousValues) => {
      const newValues = { ...previousValues };

      if (filterName === "epidemiologicalWeekBegin" && newValue.value === "") {
        newValues.epidemiologicalWeekBegin = newValue;
        newValues.epidemiologicalWeekEnd = newValue;

        return newValues;
      }

      if (
        filterName === "epidemiologicalWeekEnd" &&
        newValue.value !== "" &&
        newValues.epidemiologicalWeekBegin.value !== ""
      ) {
        const beginWeekNumber = +newValues.epidemiologicalWeekBegin.label;
        const endWeekNumber = +newValue.label;

        if (endWeekNumber < beginWeekNumber) {
          toast.error(
            "A semana epidemiológica final deve ser igual ou maior que a inicial."
          );

          return newValues;
        }

        const epidemiologicalWeeksFromYear = getWeeks().find(
          ({ year }) => year === filters.year.label
        ).weeks;

        const weeksInBetween = epidemiologicalWeeksFromYear
          .slice(beginWeekNumber - 1, endWeekNumber)
          .map(({ endDate, ...rest }) => ({
            ...rest,
            endDate,
            value: endDate,
          }));

        newValues.epidemiologicalWeeks = weeksInBetween;
        newValues[filterName] = newValue;

        return newValues;
      }

      newValues[filterName] = newValue;

      return newValues;
    });
  };

  const handleDatePeriodChange = () => {
    setFilters((previousValues) => {
      const newValues = { ...previousValues };

      const datePeriodTypeValue =
        newValues.datePeriodType === "week" ? "month" : "week";

      newValues.datePeriodType = datePeriodTypeValue;

      return newValues;
    });
  };

  const handleFiltersChange = (filterName, newValue) => {
    setFilters((previousValues) => {
      const newValues = { ...previousValues };

      newValues[filterName] = newValue;

      return newValues;
    });
  };

  const handleFiltersTypesChange = (filterTypeName) => {
    setFilters((previousValues) => {
      const newValues = { ...previousValues };

      newValues.filtersTypes[filterTypeName] =
        newValues.filtersTypes[filterTypeName] === 1 ? 2 : 1;

      return newValues;
    });
  };

  const handleMonthFilterChange = (filterName, newValue) => {
    setFilters((previousValues) => {
      const newValues = { ...previousValues };

      if (filterName === "monthBegin" && newValue.value === "") {
        newValues.monthBegin = newValue;
        newValues.monthEnd = newValue;

        return newValues;
      }

      if (
        filterName === "monthEnd" &&
        newValue.value !== "" &&
        newValues.monthBegin.value !== ""
      ) {
        const beginMonthNumber = newValues.monthBegin.value;
        const endMonthNumber = newValue.value;

        if (endMonthNumber < beginMonthNumber) {
          toast.error("o mês final deve ser igual ou maior que o inicial.");

          return newValues;
        }

        const monthsInBetween = monthsOptionsList.filter(
          ({ value }) => value >= beginMonthNumber && value <= endMonthNumber
        );

        newValues.months = monthsInBetween;
        newValues[filterName] = newValue;

        return newValues;
      }

      newValues[filterName] = newValue;

      return newValues;
    });
  };

  const handleTypeLayerChange = (value) => {
    handleFiltersChange(
      "typeLayers",
      !value || value.length === 0 ? [] : value
    );

    if (!value || value.length === 0)
      handleFiltersChange("territorializations", []);
    else {
      const allTerritorializationsToSet = [];

      value.forEach(({ territorializations }) => {
        allTerritorializationsToSet.push(...territorializations);
      });

      handleFiltersChange("territorializations", allTerritorializationsToSet);
    }
  };

  const handleYearFilterChange = (filterName, filterValue) => {
    setFilters((previousValues) => {
      const newValues = { ...previousValues };
      newValues[filterName] = filterValue;

      newValues.endDate = "";

      return newValues;
    });
  };

  return {
    filters,
    isInformationsLoading,
    fetchAllComponentsData,
    handleEpidemiologicWeeksFilterChange,
    handleDatePeriodChange,
    handleFiltersChange,
    handleFiltersTypesChange,
    handleMonthFilterChange,
    handleTypeLayerChange,
    handleYearFilterChange,
    isPopoverOpen,
    togglePopover,
  };
};

const ProductivityPageFilters = ({
  demands,
  employees,
  teams,
  territorializations,
  trapTypes,
  typeLayers,
}) => {
  const {
    filters,
    isInformationsLoading,
    fetchAllComponentsData,
    handleEpidemiologicWeeksFilterChange,
    handleDatePeriodChange,
    handleFiltersChange,
    handleMonthFilterChange,
    handleTypeLayerChange,
    handleYearFilterChange,
    isPopoverOpen,
    togglePopover,
  } = useProductivityPageFilters();

  const styles = {
    label: {
      fontWeight: "bold",
      width: "100%",
      display: "flex",
      gap: ".1rem",
    },
  };

  return (
    <>
      <Button
        className={defaultStyles.filtersButton}
        id="mainFiltersPopover"
        type="button"
      >
        <i className="fa fa-filter"></i> Filtros
      </Button>
      <Popover
        style={{ width: "500px", zIndex: 100000 }}
        offset="-120, 8"
        placement="bottom"
        target="mainFiltersPopover"
        isOpen={isPopoverOpen}
        toggle={togglePopover}
        id="filtersPopover"
      >
        <PopoverHeader>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ...styles.label,
            }}
          >
            Filtros
            <span style={{ fontSize: "12px", fontWeight: 400 }}>
              Semana epidemiológica atual: {getTodayEpidemiologicalWeek()}
            </span>
            <i
              className="fa fa-times"
              onClick={togglePopover}
              style={{ cursor: "pointer" }}
            ></i>
          </div>
        </PopoverHeader>
        <PopoverBody style={{ backgroundColor: "#ffffff" }}>
          <span
            style={{
              display: "block",
              textAlign: "right",
              fontSize: "11px",
              fontWeight: "bold",
            }}
          >
            ( <span style={{ color: "red" }}>*</span> ) : Campo obrigatório
          </span>
          <Row className="mb-2">
            <Col>
              <label htmlFor="beginDate" style={styles.label}>
                De (<span style={{ color: "red" }}>*</span>) :
              </label>
              <DateFilter
                filterName="beginDate"
                handleFiltersChange={handleFiltersChange}
                id="beginDate"
                value={filters.beginDate}
                disabled={filters.year.value !== ""}
              />
            </Col>
            <Col>
              <label htmlFor="endDate" style={styles.label}>
                Até (<span style={{ color: "red" }}>*</span>) :
              </label>
              <DateFilter
                filterName="endDate"
                handleFiltersChange={handleFiltersChange}
                id="endDate"
                value={filters.endDate}
                disabled={filters.year.value !== ""}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <label htmlFor="" style={styles.label}>
                Ano (<span style={{ color: "red" }}>*</span>) :
              </label>
              <YearsFilter
                filterName="year"
                handleFiltersChange={handleYearFilterChange}
                value={filters.year}
                isDisabled={true}
              />
            </Col>
            <Col>
              {filters.datePeriodType === "week" ? (
                <div>
                  <label htmlFor="" style={styles.label}>
                    <span style={{ color: "#3b6bca" }}>Semana</span>
                    inicio:{" "}
                    <i onClick={handleDatePeriodChange}>
                      <ExchangeIcon />
                    </i>
                  </label>
                  <EpidemiologicWeekFilter
                    filterName="epidemiologicalWeekBegin"
                    handleFiltersChange={handleEpidemiologicWeeksFilterChange}
                    value={filters.epidemiologicalWeekBegin}
                    year={String(filters.year.value)}
                    isDisabled={true}
                  />
                </div>
              ) : (
                <div>
                  <label htmlFor="" style={styles.label}>
                    <span style={{ color: "#3b6bca" }}>Mês</span>
                    inicio:{" "}
                    <i onClick={handleDatePeriodChange}>
                      <ExchangeIcon />
                    </i>
                  </label>
                  <MonthWeekFilter
                    filterName="monthBegin"
                    handleFiltersChange={handleMonthFilterChange}
                    value={filters.monthBegin}
                    isDisabled={true}
                  />
                </div>
              )}
            </Col>
            <Col>
              {filters.datePeriodType === "week" ? (
                <div>
                  <label htmlFor="" style={styles.label}>
                    <span style={{ color: "#3b6bca" }}>Semana</span>
                    fim:{" "}
                    <i onClick={handleDatePeriodChange}>
                      <ExchangeIcon />
                    </i>
                  </label>
                  <EpidemiologicWeekFilter
                    filterName="epidemiologicalWeekEnd"
                    handleFiltersChange={handleEpidemiologicWeeksFilterChange}
                    value={filters.epidemiologicalWeekEnd}
                    year={String(filters.year.value)}
                    isDisabled={true}
                  />
                </div>
              ) : (
                <div>
                  <label htmlFor="" style={styles.label}>
                    <span style={{ color: "#3b6bca" }}>Mês</span>
                    fim:{" "}
                    <i onClick={handleDatePeriodChange}>
                      <ExchangeIcon />
                    </i>
                  </label>
                  <MonthWeekFilter
                    filterName="monthEnd"
                    handleFiltersChange={handleMonthFilterChange}
                    value={filters.monthEnd}
                    isDisabled={true}
                  />
                </div>
              )}
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <label htmlFor="" style={styles.label}>
                Tipo de armadilha (<span style={{ color: "red" }}>*</span>) :
              </label>
              <TrapTypesFilter
                filterName="trapType"
                handleFiltersChange={handleFiltersChange}
                options={trapTypes}
                value={filters.trapType}
                isDisabled={true}
              />
            </Col>
            <Col>
              <label htmlFor="" style={styles.label}>
                Demanda:
              </label>
              <DemandsFilter
                filterName="demand"
                handleFiltersChange={handleFiltersChange}
                options={demands}
                value={filters.demand}
                isDisabled={true}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <label htmlFor="" style={styles.label}>
                Usuários:{" "}
              </label>
              <UsersFilter
                filterName="employees"
                handleFiltersChange={handleFiltersChange}
                options={employees}
                value={filters.employees}
                isDisabled={employees.length === 0}
              />
            </Col>
            <Col>
              <label htmlFor="" style={styles.label}>
                Equipe:{" "}
              </label>
              <TeamsFilter
                filterName="team"
                handleFiltersChange={handleFiltersChange}
                options={teams}
                value={filters.team}
                isDisabled={teams.length === 0}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <label htmlFor="" style={styles.label}>
                Delimitador de área:
              </label>
              <TypeLayersFilter
                handleTypeLayerChange={handleTypeLayerChange}
                typeLayersList={typeLayers}
                value={filters.typeLayers}
                isDisabled={typeLayers.length === 0}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col>
              <label htmlFor="" style={styles.label}>
                Territorializações:
              </label>
              <TerritorializationsFilter
                handleFiltersChange={handleFiltersChange}
                territorializationsList={territorializations}
                value={filters.territorializations}
                isDisabled={territorializations.length === 0}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              {isInformationsLoading ? (
                <Button disabled={true} color="primary">
                  <LoadingSpin color="#fff" size={17} />
                </Button>
              ) : (
                <Button
                  onClick={() => fetchAllComponentsData(filters)}
                  color="primary"
                >
                  Aplicar filtros
                </Button>
              )}
            </Col>
            <Col>
              <Button onClick={togglePopover}>Fechar</Button>
            </Col>
          </Row>
        </PopoverBody>
      </Popover>
    </>
  );
};

export const PRODUCTIVITY_PAGE_DEFAULT_FILTERS = {
  beginDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
  demand: {
    label: "Selecione",
    value: "",
  },
  datePeriodType: "week",
  disease: {
    label: "Dengue",
    value: 1,
  },
  epidemiologicalWeeks: [],
  epidemiologicalWeekBegin: {
    label: "Selecione",
    value: "",
  },
  epidemiologicalWeekEnd: {
    label: "Selecione",
    value: "",
  },
  employees: [],
  months: [],
  monthBegin: {
    label: "Selecione",
    value: "",
  },
  monthEnd: {
    label: "Selecione",
    value: "",
  },
  endDate: moment().format("YYYY-MM-DD"),
  filtersTypes: {
    usersIds: 1,
  },
  team: {
    label: "Selecione",
    value: "",
  },
  territorializations: [],
  trapType: {
    label: "Selecione",
    value: "",
  },
  typeLayers: [],
  users: [],
  year: {
    label: "Selecione",
    value: "",
  },
};
